import { useEffect, useState } from "react";
import { Checkbox, Grid, Typography } from "@mui/material";

import { Plan } from "../../../../../models/plan";
import { contactTaskColumns } from "./grid-columns";
import { EditContactDialog } from "./edit-contact/edit-contact.dialog";
import { contactApiKeyStore } from "../../../../../api/contact-api-key-store";
import { UserPermissionName } from "../../../../../models/user-permission-name";
import { SiTrackGrid } from "../../../../../components/si-track-grid/si-track-grid";
import { planReviewApiKeyStore } from "../../../../../api/plan-review-api-key-store";
import { useLoadData } from "../../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../../models/api-response";
import { GridColumnConfig, GridPreference } from "../../../../../models/gridCoumnConfig";
import { gridApiKeyStore } from "../../../../../api/grid-api-key-store";
import { useSaveData } from "../../../../../hooks/useSaveData";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../../hooks/use-debounce";

interface CellProps {
    moduleData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface ContactDetailsComponent {
    title: string;
    planReviewTaskData: any;
    isLoading: boolean;
    selectedContactsData: (contacts: any) => void;
}

export const ContactDetailsComponent = ({ title, planReviewTaskData, selectedContactsData, isLoading }: ContactDetailsComponent) => {

    const [editItem, setEditItem] = useState<Partial<Plan>>();
    const [updatedContactData, setContactData] = useState<any>();

    useEffect(() => {
        if (planReviewTaskData) {
            const updatedData = planReviewTaskData?.unitContacts?.map((cd: any) => ({
                ...cd,
                isSelected: cd?.isPlanReviewMapped,
            }));
            setContactData(updatedData);
            selectedContactsData(updatedData?.filter((con: any) => con?.isSelected == true));
        }
    }, [planReviewTaskData, isLoading]);

    const handleEditContact = (contactData: any) => {
        setEditItem(contactData);
    }

    const handleEditClose = () => {
        setEditItem(undefined);
    };

    const SelectContactCell = ({ moduleData }: CellProps) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const updatedData = updatedContactData?.map((d: any) => ({
                ...d,
                isSelected: (moduleData?.contactsID == d?.contactsID) ? event.target.checked : d?.isSelected,
            }));
            setContactData(updatedData);
            selectedContactsData(updatedData?.filter((con: any) => con?.isSelected == true));
        };

        return (
            <td>
                <Checkbox
                    checked={updatedContactData?.find((pl: any) => pl.contactsID == moduleData?.contactsID)?.isSelected}
                    onChange={handleChange}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'select') {
            return (dataItem: any) => {
                return <SelectContactCell moduleData={dataItem.dataItem} />;
            };
        }
    }

    // -------------------------------------------------Grid Layout------------------------------
    const titleName = title?.split(" ")?.join("");
    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout(`${titleName}PlanReviewContact`, `${titleName}PlanReviewContact`)
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredContacts, setFilteredContacts] = useState<Plan[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredContactList = planReviewTaskData?.unitContacts?.filter((Contact: any) =>
            Object.values(Contact).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredContacts(filteredContactList || []);
    }, [debouncedSearchTerm, planReviewTaskData?.unitContacts]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: `${titleName}PlanReviewContact`,
            pageName: `${titleName}PlanReviewContact`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: `${titleName}PlanReviewContact`,
            pageName: `${titleName}PlanReviewContact`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    return (
        <Grid container spacing={1}>
            <Grid className="subHead" item lg={12} sm={12} xl={12} xs={12}>
                <Typography variant="h3" color="textPrimary">
                    CONTACTS
                </Typography>
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='plan_review_contact'
                    columns={contactTaskColumns}
                    dataItemKey="contactsID"
                    customCell={(field) =>
                        CustomGridCell({
                            field,
                        })
                    }
                    currentPageSize={5}
                    onEdit={handleEditContact}
                    actionPermissions={{
                        edit: UserPermissionName.EDIT_CONTACT,
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={isLoading || isLoadingGridLayout}

                    // -----------------Grid Layout-------
                    dataItems={filteredContacts || []}
                    reorderable
                    handleSearch={handleInputChange}
                    handleSaveLayout={handleSaveLayout}
                    isSaveLayout={true}
                    gridConfigFromApi={gridConfig}
                    handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
            </Grid>
            {editItem && (
                <EditContactDialog
                    data={editItem}
                    config={{
                        saveConfig: contactApiKeyStore.updateContact(),
                        invalidateRoute: () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID),
                    }}
                    // yupSchema={planFormSchema}
                    onClosed={handleEditClose} />
            )}
        </Grid>
    );
}